import { useState } from "react";
import type { Asset } from "@yushi-portfolio/content-schema";
import { AssetThumb, assetLabel, TYPE_LABEL, UploadButton } from "../components/AssetUI";
import { TextInput } from "../components/fields";
import type { ApiClient } from "../lib/api";
import { useAssets } from "../lib/assets";
import { assetStatus } from "../lib/upload";

export function AssetsPage({ api }: { api: ApiClient }) {
  const { assets, error } = useAssets();
  return (
    <section>
      <header className="page-head">
        <h1>Assets</h1>
        <UploadButton api={api} />
      </header>
      <p className="muted">
        原版は非公開で保存され、Web 用の画像 (640 / 1280 / 1920px, AVIF・WebP・JPEG) が自動で作られます。映像は Web 再生用の MP4 / WebM をアップロードしてください。
      </p>
      {error && <div className="notice error">{error}</div>}
      {!assets && <p className="muted">読み込み中…</p>}
      {assets?.length === 0 && <p className="muted">まだありません。</p>}
      <div className="stack">
        {assets?.map((asset) => <AssetRow key={asset.id} api={api} asset={asset} />)}
      </div>
    </section>
  );
}

function AssetRow({ api, asset }: { api: ApiClient; asset: Asset }) {
  const { upsert } = useAssets();
  const [photographer, setPhotographer] = useState(asset.photographer ?? "");
  const [copyright, setCopyright] = useState(asset.copyright ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");
  const dirty = photographer !== (asset.photographer ?? "") || copyright !== (asset.copyright ?? "");
  const status = assetStatus(asset);

  async function save() {
    setState("saving");
    try {
      upsert(await api.updateAsset(asset.id, { photographer, copyright }));
      setState("saved");
    } catch (e) {
      setState((e as Error).message);
    }
  }

  return (
    <div className="card-row asset-row">
      <AssetThumb asset={asset} size={120} />
      <div className="stack-tight grow">
        <strong>{assetLabel(asset, asset.id)}</strong>
        <span className="muted">
          {TYPE_LABEL[asset.type]} · {status === "processing" ? "変換中…" : status === "ready" ? "公開用データあり" : "保存済み (非公開)"}
          {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ""}
          {asset.durationSeconds ? ` · ${asset.durationSeconds}秒` : ""}
        </span>
        <span className="mono">{asset.id}</span>
        <div className="grid-2">
          <TextInput label="撮影者" value={photographer} onChange={setPhotographer} />
          <TextInput label="著作権表示" value={copyright} onChange={setCopyright} />
        </div>
        <div className="actions">
          <button type="button" disabled={!dirty || state === "saving"} onClick={save}>
            {state === "saving" ? "保存中…" : "保存"}
          </button>
          {state === "saved" && !dirty && <span className="muted">保存しました</span>}
          {state !== "idle" && state !== "saving" && state !== "saved" && <span className="error-text">{state}</span>}
        </div>
      </div>
    </div>
  );
}
